import React, {useEffect} from 'react';
import {useModalManager} from '@basicComponents/sequential-modal/ModalManagerProvider';
import globalStore from '@services/global.state';
import RechargeGiftModal from './RechargeGiftModal';
import {
  getCurrentRechargeGift,
  type RechargeGiftScene,
} from './recharge-gift.service';
import {rechargeGiftModalRequest} from './recharge-gift-trigger';
import {
  isRechargeGiftCooldownActive,
  startRechargeGiftCooldown,
} from './recharge-gift-cooldown';
import {hasAvailableRechargeGiftTier} from './recharge-gift-eligibility';

const waitForModalManagerIdle = (
  currentModalId: React.MutableRefObject<string | undefined>,
) =>
  new Promise<boolean>(resolve => {
    const startedAt = Date.now();
    const check = () => {
      if (!currentModalId.current) {
        resolve(true);
        return;
      }
      if (Date.now() - startedAt >= 1200) {
        resolve(false);
        return;
      }
      setTimeout(check, 50);
    };
    check();
  });

const isRechargeEntryInterceptDisabled = (
  scene: RechargeGiftScene,
  rechargeInterceptStatus?: number | null,
) => scene === 'RECHARGE_ENTRY' && rechargeInterceptStatus === 0;

/** 全局处理主动触发的充值礼包；首页顺序弹窗仍由 HomeSequential 独立处理。 */
export default function RechargeGiftModalController() {
  const {showModal, currentModalId} = useModalManager();

  useEffect(() => {
    const subscription = rechargeGiftModalRequest.subscribe(request => {
      const handleIneligible = () => {
        request.onIneligible?.();
        request.onUnavailable?.();
      };

      if (!globalStore.token) {
        handleIneligible();
        return;
      }

      getCurrentRechargeGift(request.scene)
        .then(gift => {
          if (
            isRechargeEntryInterceptDisabled(
              request.scene,
              gift.rechargeInterceptStatus,
            )
          ) {
            request.onUnavailable?.();
            return;
          }
          if (!hasAvailableRechargeGiftTier(gift)) {
            handleIneligible();
            return;
          }
          if (
            request.scene === 'RECHARGE_ENTRY' &&
            isRechargeGiftCooldownActive(
              request.scene,
              gift.interceptCooldownHours,
            )
          ) {
            request.onUnavailable?.();
            return;
          }

          const handleDismiss = () => {
            if (request.scene === 'RECHARGE_ENTRY') {
              startRechargeGiftCooldown(request.scene);
            }
            request.onDismiss?.();
          };

          return waitForModalManagerIdle(currentModalId).then(isIdle => {
            if (!isIdle) {
              request.onUnavailable?.();
              return;
            }
            const shown = showModal({
              id: `recharge-gift-${request.scene}`,
              overlayClassName: 'bg-black/80',
              modals: [
                <RechargeGiftModal
                  gift={gift}
                  scene={request.scene}
                  onDismiss={handleDismiss}
                  sequentialOverlayClassName="bg-black/80"
                  sequentialTransitionDuration={0.18}
                  sequentialTransitionType="zoom-in-out"
                />,
              ],
            });
            if (!shown) {
              request.onUnavailable?.();
            }
          });
        })
        .catch(handleIneligible);
    });

    return () => subscription.unsubscribe();
  }, [currentModalId, showModal]);

  return null;
}
